import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  submitGeneration,
  getGenerationStatus,
  getGenerationResult,
  fetchGenerationContent,
} from '../services/inferenceApi';
import {
  isNotReadyResult,
  extractMediaItems,
  extractTextOutput,
  mediaKind,
  resolveMediaFilename,
  MediaItem,
} from '../utils/generation';

// Polling cadence: 2s, slowing to 4s once a run has sat in "queued" for
// over 60s, with a hard stop (client-side "timeout" status) at 10 minutes.
const POLL_INTERVAL_FAST_MS = 2000;
const POLL_INTERVAL_SLOW_MS = 4000;
const SLOWDOWN_AFTER_MS = 60000;
const TIMEOUT_MS = 10 * 60 * 1000;

// Terminal-failure message when the result carried media items but every
// content_url fetch failed. Exact string is also the refresh() re-entry
// guard's marker for "this failure is retryable", so keep them in sync.
const MEDIA_DOWNLOAD_FAILED_ERROR = 'Generated media could not be downloaded - try Refresh';

// Filesystem/URL-safe slug for the filename prefix, e.g. "fal-ai/flux/dev" -> "fal-ai-flux-dev".
function slugify(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'model';
}

// Extracts a string `error` field from a status/result JSON body, if present.
function extractErrorString(json: unknown): string | undefined {
  if (typeof json !== 'object' || json === null || Array.isArray(json)) return undefined;
  const err = (json as Record<string, unknown>).error;
  return typeof err === 'string' ? err : undefined;
}

function computeDelay(elapsedMs: number, status: string): number {
  return status === 'queued' && elapsedMs > SLOWDOWN_AFTER_MS ? POLL_INTERVAL_SLOW_MS : POLL_INTERVAL_FAST_MS;
}

export interface ResolvedMedia {
  objectUrl: string;
  kind: 'image' | 'video' | 'audio' | 'file';
  contentType?: string;
  filename: string;
  // Absolute, signed, public link (see MediaItem.artifactUrl) for sharing -
  // playback/download continue to use objectUrl (the authed blob) above,
  // unchanged.
  artifactUrl?: string;
}

export interface GenerationRun {
  id: string;
  status: 'queued' | 'in_progress' | 'completed' | 'failed' | 'timeout';
  queuePosition?: number;
  startedAt: number;
  media: ResolvedMedia[];
  textOutput: string | null;
  error?: string;
  // Set on a completed run when resolveMedia() downloaded fewer items than
  // extractMediaItems() found (but at least one) - a partial success, as
  // opposed to items.length > 0 && media.length === 0 which fails the run
  // entirely via MEDIA_DOWNLOAD_FAILED_ERROR below.
  warning?: string;
}

export interface UseGenerationResult {
  runs: GenerationRun[];
  active: GenerationRun | null;
  submit: (body: Record<string, unknown>) => Promise<void>;
  refresh: (runId: string) => void;
  isSubmitting: boolean;
}

// Pure predicate for whether runId should still be treated as live: present
// in `runs` AND not explicitly marked cleared in `clearedRunIds`. Exported
// (and kept argument-driven rather than closing over refs) so it's directly
// unit-testable independent of the hook's async/timing machinery.
//
// clearedRunIds is checked FIRST and is authoritative on its own - a plain
// ref mutated synchronously the instant submit() decides to clear a run.
// Checking only `runs.some(...)` is NOT sufficient: `runs` here is whatever
// the caller captured (typically runsRef.current), and runsRef.current is
// only refreshed by the `runsRef.current = runs` assignment that runs on
// each render - which has NOT necessarily happened yet in the instant right
// after submit() calls setRuns([]) (React defers the re-render). So a check
// against a stale `runs` snapshot could still find the "cleared" run and
// call it live. clearedRunIds closes that window.
export function isRunLive(runId: string, runs: GenerationRun[], clearedRunIds: ReadonlySet<string>): boolean {
  if (clearedRunIds.has(runId)) return false;
  return runs.some((r) => r.id === runId);
}

// True iff run can be re-entered via refresh(): a run stuck in the
// client-side 'timeout' state, one that failed specifically because its
// (already-generated) media couldn't be downloaded, or a completed run that
// only partially downloaded its media (carries a `warning`). A genuinely
// completed run with no warning, or one failed for another reason (or one
// still actively polling) is not retryable. Single source of truth for the
// MEDIA_DOWNLOAD_FAILED_ERROR comparison - components must use this instead
// of duplicating the string.
export function isRetryable(run: GenerationRun): boolean {
  return (
    run.status === 'timeout' ||
    (run.status === 'failed' && run.error === MEDIA_DOWNLOAD_FAILED_ERROR) ||
    (run.status === 'completed' && !!run.warning)
  );
}

// Polls an async generation job to completion (or timeout) and resolves its
// media into blob object URLs. Kept thin: every status-advance decision
// delegates to the pure helpers in utils/generation; this hook only owns
// scheduling, React state, and cleanup (timers + object URLs).
export function useGeneration(modelId: string, category: string, apiKeyOverride: string | null): UseGenerationResult {
  const [runs, setRuns] = useState<GenerationRun[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Guards every state update after unmount; timers/URLs are also cleared
  // on unmount so no orphaned poll can fire after this ref flips.
  const abortedRef = useRef(false);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Object URLs keyed by the run that created them, so a partial-download
  // retry (see refresh()) can revoke and forget only THAT run's blobs
  // instead of every run's. Cleaned up per-run in revokeRunUrls(), and in
  // bulk on unmount below.
  const objectUrlsRef = useRef<Map<string, Set<string>>>(new Map());
  // Structural double-entry guard: a runId is present while a poll() call
  // for it is executing (added on entry, removed in a finally). refresh()
  // consults this so it can never start a second concurrent poll for a
  // run whose previous tick hasn't finished its awaits yet.
  const pollingRef = useRef<Set<string>>(new Set());
  // Run ids submit() has cleared (see isRunLive doc comment above for why
  // this - not just an absence check against runsRef - is the authoritative
  // signal). Entries are never removed: a run id is only ever added once
  // (when a later submit() clears it) and the Set holds plain short id
  // strings, so unbounded growth over a very long single-tab session is a
  // few bytes per generation - not worth the complexity of pruning it.
  const clearedRunsRef = useRef<Set<string>>(new Set());

  // Always-current values for use inside poll closures scheduled earlier,
  // so a queued setTimeout callback never acts on a stale modelId/apiKey,
  // and so refresh() can synchronously read the latest run status/error
  // without depending on the `runs` closure from whatever render it was
  // defined in.
  const modelIdRef = useRef(modelId);
  const categoryRef = useRef(category);
  const apiKeyRef = useRef(apiKeyOverride);
  const runsRef = useRef<GenerationRun[]>(runs);
  modelIdRef.current = modelId;
  categoryRef.current = category;
  apiKeyRef.current = apiKeyOverride;
  runsRef.current = runs;

  useEffect(() => {
    abortedRef.current = false;
    const timers = timersRef.current;
    const objectUrls = objectUrlsRef.current;
    return () => {
      abortedRef.current = true;
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
      objectUrls.forEach((urls) => urls.forEach((url) => URL.revokeObjectURL(url)));
      objectUrls.clear();
    };
  }, []);

  function patchRun(runId: string, patch: Partial<GenerationRun> | ((run: GenerationRun) => Partial<GenerationRun>)) {
    if (abortedRef.current) return;
    setRuns((prev) =>
      prev.map((r) => (r.id === runId ? { ...r, ...(typeof patch === 'function' ? patch(r) : patch) } : r))
    );
  }

  function clearTimer(runId: string) {
    const timer = timersRef.current.get(runId);
    if (timer !== undefined) {
      clearTimeout(timer);
      timersRef.current.delete(runId);
    }
  }

  // Revokes and forgets only this run's object URLs (see objectUrlsRef).
  // Used by refresh() before re-polling a partially-downloaded run, so the
  // stale blobs from the previous attempt don't leak.
  function revokeRunUrls(runId: string) {
    const urls = objectUrlsRef.current.get(runId);
    if (!urls) return;
    urls.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current.delete(runId);
  }

  // Thin closure over isRunLive binding the two hook-local refs it needs -
  // every call site below reads through this instead of the exported pure
  // function directly.
  function isLive(runId: string): boolean {
    return isRunLive(runId, runsRef.current, clearedRunsRef.current);
  }

  function schedule(runId: string, startedAt: number, delay: number, lastStatus: string) {
    if (abortedRef.current) return;
    clearTimer(runId);
    const timer = setTimeout(() => {
      void poll(runId, startedAt, lastStatus);
    }, delay);
    timersRef.current.set(runId, timer);
  }

  async function resolveMedia(runId: string, items: MediaItem[]): Promise<ResolvedMedia[]> {
    const media: ResolvedMedia[] = [];
    const modelSlug = slugify(modelIdRef.current);
    for (let i = 0; i < items.length; i++) {
      if (abortedRef.current) return media;
      if (!isLive(runId)) {
        // A concurrent submit() cleared this run (see isRunLive) since the
        // last checkpoint - possibly after an earlier iteration already
        // created and stored an object URL for it (submit()'s own
        // revokeRunUrls call already revoked/deleted that, since it ran
        // before this run vanished from state). Nothing left to revoke here
        // in the common case, but call revokeRunUrls anyway as a no-op-safe
        // guard against any URL landing here by future refactor.
        revokeRunUrls(runId);
        return media;
      }
      const item = items[i];
      let blob: Blob;
      try {
        blob = await fetchGenerationContent(item.contentUrl, apiKeyRef.current);
      } catch {
        // Skip items that fail to download rather than failing the whole run.
        continue;
      }
      if (abortedRef.current) return media;
      if (!isLive(runId)) {
        // The clear happened while this item's fetch was in flight. Bail
        // before ever creating an object URL for it - this is the exact
        // leak this guard exists for: without it, the line below would
        // call `objectUrlsRef.current.get(runId)`, find nothing (submit()
        // already deleted that entry), silently create a fresh Set, and
        // stash an object URL nothing will ever revoke again.
        revokeRunUrls(runId);
        return media;
      }
      const objectUrl = URL.createObjectURL(blob);
      if (!isLive(runId)) {
        // Belt-and-suspenders: there's no `await` between the check above
        // and this createObjectURL call, so this can't currently trigger,
        // but it protects against a future refactor introducing one -
        // revoke the URL just created rather than storing it.
        URL.revokeObjectURL(objectUrl);
        revokeRunUrls(runId);
        return media;
      }
      let runUrls = objectUrlsRef.current.get(runId);
      if (!runUrls) {
        runUrls = new Set<string>();
        objectUrlsRef.current.set(runId, runUrls);
      }
      runUrls.add(objectUrl);
      media.push({
        objectUrl,
        kind: mediaKind(item, categoryRef.current),
        contentType: item.contentType,
        filename: resolveMediaFilename(item, modelSlug, runId, i + 1),
        artifactUrl: item.artifactUrl,
      });
    }
    return media;
  }

  async function poll(runId: string, startedAt: number, lastStatus: string): Promise<void> {
    if (abortedRef.current) return;
    // Structural double-entry guard: refresh() can call poll() directly
    // while a scheduled tick for the same run is still mid-await. Skip
    // rather than run two concurrent polls (which could double-fetch and
    // double-createObjectURL for the same completed result).
    if (pollingRef.current.has(runId)) return;
    pollingRef.current.add(runId);
    try {
      const elapsed = Date.now() - startedAt;
      if (elapsed >= TIMEOUT_MS) {
        patchRun(runId, { status: 'timeout' });
        clearTimer(runId);
        return;
      }

      let statusResp;
      try {
        statusResp = await getGenerationStatus(modelIdRef.current, runId, apiKeyRef.current);
      } catch {
        if (abortedRef.current) return;
        // Transient network/backend hiccup - keep polling at the current cadence.
        schedule(runId, startedAt, computeDelay(elapsed, lastStatus), lastStatus);
        return;
      }
      if (abortedRef.current) return;
      if (!isLive(runId)) {
        // Cleared by a concurrent submit() while the status fetch was in
        // flight - stop before doing any more work (including scheduling
        // another tick) for a run nothing will ever render again.
        clearTimer(runId);
        return;
      }

      const elapsedAfterFetch = Date.now() - startedAt;
      if (elapsedAfterFetch >= TIMEOUT_MS) {
        patchRun(runId, { status: 'timeout' });
        clearTimer(runId);
        return;
      }

      const status = statusResp.status;

      if (status === 'failed') {
        patchRun(runId, { status: 'failed', error: extractErrorString(statusResp) ?? 'Generation failed' });
        clearTimer(runId);
        return;
      }

      if (status === 'completed') {
        let result: unknown;
        try {
          result = await getGenerationResult(modelIdRef.current, runId, apiKeyRef.current);
        } catch (e) {
          if (abortedRef.current) return;
          patchRun(runId, { status: 'failed', error: e instanceof Error ? e.message : 'Generation failed' });
          clearTimer(runId);
          return;
        }
        if (abortedRef.current) return;
        if (!isLive(runId)) {
          // Cleared while the result fetch was in flight - bail before
          // calling resolveMedia() (and its content_url fetches +
          // createObjectURL calls) for a run that's already gone.
          clearTimer(runId);
          return;
        }

        if (isNotReadyResult(result)) {
          // Status says completed but the result body isn't ready yet - keep polling.
          schedule(runId, startedAt, computeDelay(elapsedAfterFetch, 'queued'), 'queued');
          return;
        }

        const items = extractMediaItems(result);
        const media = items.length > 0 ? await resolveMedia(runId, items) : [];
        if (abortedRef.current) return;
        if (!isLive(runId)) {
          // resolveMedia() already guards its own loop against creating
          // (or leaking) object URLs for a run that vanished mid-flight -
          // this is just the poll()-level mirror so a dead run's status
          // never gets patched (patchRun is already a safe no-op here too,
          // per the isRunLive doc comment, but skip the wasted work).
          clearTimer(runId);
          return;
        }

        if (items.length > 0 && media.length === 0) {
          // The result named media but every content_url fetch failed -
          // don't report a silent success with an empty gallery.
          patchRun(runId, { status: 'failed', error: MEDIA_DOWNLOAD_FAILED_ERROR });
          clearTimer(runId);
          return;
        }

        const textOutput = extractTextOutput(result);
        const warning =
          media.length > 0 && media.length < items.length
            ? `Some media failed to download (${media.length} of ${items.length} shown)`
            : undefined;
        patchRun(runId, { status: 'completed', media, textOutput, error: undefined, warning });
        clearTimer(runId);
        return;
      }

      // queued | in_progress (or any other non-terminal upstream status).
      const nextStatus: GenerationRun['status'] = status === 'in_progress' ? 'in_progress' : 'queued';
      patchRun(runId, { status: nextStatus, queuePosition: statusResp.queue_position });
      schedule(runId, startedAt, computeDelay(elapsedAfterFetch, nextStatus), nextStatus);
    } finally {
      pollingRef.current.delete(runId);
    }
  }

  const submit = async (body: Record<string, unknown>): Promise<void> => {
    setIsSubmitting(true);
    try {
      // A new submission replaces the visible history entirely - only the
      // just-submitted job should render. Tear down each existing run's
      // timer/object URLs up front, mark it cleared (clearedRunsRef -
      // synchronous and immediate, unlike runsRef.current which only
      // reflects this until the next render), then clear the list before
      // pushing the new run below. Deliberately NOT touched: pollingRef
      // entries for any run whose poll() is still mid-await - that call
      // runs to completion in the background, but isLive()/isRunLive now
      // makes every one of its post-await checkpoints (and patchRun, via
      // `prev.map` finding no matching `r.id` once the run is gone from
      // `runs`) a safe no-op/bail for that dead run.
      runsRef.current.forEach((run) => {
        clearTimer(run.id);
        revokeRunUrls(run.id);
        clearedRunsRef.current.add(run.id);
      });
      setRuns([]);
      const job = await submitGeneration(modelIdRef.current, body, apiKeyRef.current);
      if (abortedRef.current) return;
      const startedAt = Date.now();
      const initialStatus: GenerationRun['status'] = job.status === 'in_progress' ? 'in_progress' : 'queued';
      const newRun: GenerationRun = {
        id: job.id,
        status: initialStatus,
        queuePosition: job.queue_position,
        startedAt,
        media: [],
        textOutput: null,
      };
      setRuns((prev) => [newRun, ...prev]);
      schedule(job.id, startedAt, POLL_INTERVAL_FAST_MS, initialStatus);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to submit generation');
    } finally {
      if (!abortedRef.current) setIsSubmitting(false);
    }
  };

  const refresh = (runId: string): void => {
    if (abortedRef.current) return;
    const run = runsRef.current.find((r) => r.id === runId);
    if (!run) return;
    if (!isRetryable(run)) return;
    // Belt-and-braces: even for a retryable status, never start a second
    // concurrent poll if one is somehow still in flight for this run.
    if (pollingRef.current.has(runId)) return;
    clearTimer(runId);
    // A completed-with-warning run being retried still holds its earlier
    // partial media as object URLs; revoke and forget only this run's
    // entries (revokeRunUrls is a no-op for timeout/download-failed runs,
    // which never resolved any media) before re-polling from scratch.
    revokeRunUrls(runId);
    const startedAt = Date.now();
    patchRun(runId, { startedAt, status: 'in_progress', error: undefined, warning: undefined, media: [] });
    void poll(runId, startedAt, 'in_progress');
  };

  const active = runs.find((r) => r.status === 'queued' || r.status === 'in_progress') ?? null;

  return { runs, active, submit, refresh, isSubmitting };
}
